// Кабинет партнёра ходит на тот же API (api.ttex.online), что и мини-апп, но авторизуется
// не по initData Telegram, а по cookie-сессии (magic-link из бота → /auth/exchange).
// Поэтому все запросы идут с credentials: "include"; CORS с partner-origin включён на API.
export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export class ApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function throwApiError(r: Response): Promise<never> {
  let detail = "";
  try {
    const body = (await r.json()) as { detail?: unknown };
    if (typeof body?.detail === "string") detail = body.detail;
  } catch {
    /* non-JSON error body */
  }
  throw new ApiError(r.status, detail);
}

export function friendlyError(e: unknown, fallback = "Что-то пошло не так. Попробуйте позже."): string {
  if (!(e instanceof ApiError)) return fallback;
  const d = (e.detail || "").toLowerCase();
  if (d.includes("service_unavailable") || d.includes("quote_unavailable")) {
    return "Сервис временно недоступен. Попробуйте чуть позже.";
  }
  switch (e.status) {
    case 400:
      if (d.includes("оферт")) return "Нужно согласиться с офертой партнёра.";
      if (d.includes("markup") || d.includes("наценк") || d.includes("потол")) return "Наценка выше допустимого максимума.";
      if (d.includes("amount") || d.includes("сумм") || d.includes("минимал")) return "Недопустимая сумма выплаты.";
      if (d.includes("card")) return "Проверьте данные карты.";
      if (d.includes("wallet") || d.includes("address")) return "Неверный адрес кошелька TRC20.";
      if (d.includes("swift")) return "Проверьте реквизиты SWIFT.";
      return "Проверьте введённые данные.";
    case 401:
      return "Сессия не активна. Войдите заново по ссылке из бота.";
    case 403:
      return "Действие сейчас недоступно.";
    case 404:
      return "Не найдено.";
    case 409:
      return e.detail || "Действие уже выполнено или конфликтует с текущим состоянием.";
    case 429:
      return "Слишком часто. Подождите немного и попробуйте снова.";
    case 502:
    case 503:
      return "Сервис временно недоступен. Попробуйте чуть позже.";
    default:
      return fallback;
  }
}

// initData Telegram, если кабинет открыт как Mini App — шлём на каждом запросе, чтобы
// авторизация не зависела от cookie (в webview Telegram третьесторонние куки бывают капризны).
function tgInitHeader(): Record<string, string> {
  const d = (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData;
  return d ? { "X-Telegram-Init-Data": d } : {};
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  // таймаут, чтобы запрос не висел бесконечно (Telegram in-app browser на мобильной сети
  // мог зависать на первом кросс-доменном POST → «долго-долго крутилось»)
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 15000);
  const headers: Record<string, string> = { ...tgInitHeader() };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let r: Response;
  try {
    r = await fetch(API_URL + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "include",
      signal: ctrl.signal,
    });
  } finally {
    clearTimeout(timer);
  }
  if (!r.ok) await throwApiError(r);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

const apiGet = <T,>(path: string) => req<T>("GET", path);
const apiPost = <T,>(path: string, body?: unknown) => req<T>("POST", path, body);
const apiPut = <T,>(path: string, body?: unknown) => req<T>("PUT", path, body);
const apiDelete = <T,>(path: string) => req<T>("DELETE", path);

// ─────────────────────────── типы ───────────────────────────
export type PartnerStatus = "pending" | "approved" | "rejected" | "blocked";
export type PartnerModel = "referral" | "agent";
export type PartnerPayoutMethod = "card" | "swift" | "crypto" | "internal";

export type PartnerProgram = {
  l1_pct: number;
  l2_pct: number;
  hold_days: number;
  min_payout_usd: number;
  payout_fees_usd: Record<PartnerPayoutMethod, number>;
  max_markup_pct: number | null;
  offer_version: string;
};

export type PartnerBalance = {
  pending_usd: number;
  hold_usd: number;
  available_usd: number;
  paid_usd: number;
  total_earned_usd: number;
};

export type PartnerStats = {
  turnover_rub: number;
  deals: number;
  clients: number;
  active_clients: number;
};

export type PartnerVerification = {
  identity_verified: boolean;
  broker_onboarded: boolean;
  can_payout_crypto: boolean;
  can_payout_fiat: boolean;
  can_transfer_internal: boolean;
};

export type PartnerMe =
  | { is_partner: false; program: PartnerProgram }
  | {
      is_partner: true;
      status: PartnerStatus;
      model: PartnerModel;
      level: string;
      reject_reason: string | null;
      code: string | null;
      clicks: number;
      signups: number;
      markup_pct: number;
      base_rate: number | null;
      client_rate: number | null;
      balance: PartnerBalance;
      stats: PartnerStats;
      verification: PartnerVerification;
      program: PartnerProgram;
      created_at: string | null;
    };

export type PartnerReferral = {
  id: string;
  attached_at: string | null;
  kyc: "verified" | "pending" | "rejected" | "none";
  turnover_rub: number;
  earned_usd: number;
};

export type PartnerSubAgent = { id: string; turnover_rub: number; earned_usd: number };

export type PartnerAccrual = {
  id: string;
  level: "L1" | "L2";
  model: PartnerModel;
  turnover_rub: number;
  rate: number;
  rate_pct: number;
  amount_usd: number;
  status: "pending" | "hold" | "available" | "cancelled";
  needs_review: boolean;
  available_at: string | null;
  created_at: string | null;
};

export type PartnerPayout = {
  id: string;
  reference: string;
  method: PartnerPayoutMethod;
  destination: Record<string, string>;
  amount_usd: number;
  fee_usd: number;
  net_usd: number;
  rate: number | null;
  amount_rub: number | null;
  status: "pending" | "completed" | "rejected" | "cancelled";
  reject_reason: string | null;
  tx_hash: string | null;
  created_at: string | null;
  completed_at: string | null;
};

export type CardDest = { card_number: string; first_name: string; last_name: string };
export type SwiftDest = {
  beneficiary_name: string;
  iban: string;
  swift_bic: string;
  bank_name?: string;
  bank_country?: string;
};
export type CryptoDest = { wallet_address: string };

export type CreatePayoutIn = {
  method: PartnerPayoutMethod;
  amount_usd: number;
  card?: CardDest;
  swift?: SwiftDest;
  crypto?: CryptoDest;
};

// Профиль кабинета (сессия magic-link)
export type CabinetMe = {
  user_id: string;
  phone: string | null;
  registered: boolean;
  partner: { status: string; model: string; code: string | null; markup_pct: number } | null;
};

export const cabinetApi = {
  exchange: (token: string) =>
    apiPost<CabinetMe>("/v1/partner-cabinet/auth/exchange", { token }),
  // Вход по initData, когда кабинет открыт как Telegram Mini App (кнопка-WebApp из бота).
  telegram: (initData: string) =>
    req<CabinetMe>("POST", "/v1/partner-cabinet/auth/telegram", { init_data: initData }),
  me: () => apiGet<CabinetMe>("/v1/partner-cabinet/me"),
  logout: () => apiPost<{ ok: boolean }>("/v1/partner-cabinet/logout"),
};

export type PartnerBot = {
  id: string;
  tg_bot_id: number;
  username: string | null;
  title: string | null;
  enabled: boolean;
  code: string | null;
  link: string | null;
  markup_pct: number | null;      // ставка (надбавка агента) этого бота, %
  markup_overridden: boolean;      // задана своя ставка бота (не общая партнёрская)
  created_at: string | null;
};

export const partnerApi = {
  me: () => apiGet<PartnerMe>("/v1/partner/me"),
  bots: () => apiGet<{ bots: PartnerBot[] }>("/v1/partner/bots"),
  addBot: (token: string, title?: string) =>
    apiPost<PartnerBot>("/v1/partner/bots", { token, title: title || null }),
  toggleBot: (id: string, enabled: boolean) =>
    apiPost<PartnerBot>(`/v1/partner/bots/${id}/toggle`, { enabled }),
  setBotMarkup: (id: string, markup_pct: number) =>
    apiPost<PartnerBot>(`/v1/partner/bots/${id}/markup`, { markup_pct }),
  deleteBot: (id: string) => apiDelete<{ ok: boolean }>(`/v1/partner/bots/${id}`),
  offer: () => apiGet<{ version: string; text: string }>("/v1/partner/offer"),
  apply: (model: PartnerModel) =>
    apiPost<{ status: PartnerStatus; model: PartnerModel }>("/v1/partner/apply", {
      model,
      offer_accepted: true,
    }),
  setMarkup: (markup_pct: number) =>
    apiPut<{ markup_pct: number; client_rate: number }>("/v1/partner/markup", { markup_pct }),
  referrals: () =>
    apiGet<{ clients: PartnerReferral[]; sub_agents: PartnerSubAgent[] }>("/v1/partner/referrals"),
  accruals: () => apiGet<{ accruals: PartnerAccrual[] }>("/v1/partner/accruals"),
  payouts: () => apiGet<{ payouts: PartnerPayout[] }>("/v1/partner/payouts"),
  createPayout: (body: CreatePayoutIn) => apiPost<PartnerPayout>("/v1/partner/payouts", body),
  cancelPayout: (id: string) => apiPost<PartnerPayout>(`/v1/partner/payouts/${id}/cancel`),
};
